import React, { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabase';
import { Plus, Edit, Trash2, X, ChevronDown, MoreVertical, Copy } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { format, parseISO } from 'date-fns';

const categories = [
  { value: 'general', label: 'General', color: '#6b7280' },
  { value: 'load_in', label: 'Load In', color: '#3b82f6' },
  { value: 'rehearsal', label: 'Rehearsal', color: '#f59e0b' },
  { value: 'show', label: 'Show/Performance', color: '#10b981' },
  { value: 'break', label: 'Break', color: '#6b7280' },
  { value: 'meal', label: 'Meal', color: '#8b5cf6' },
  { value: 'load_out', label: 'Load Out', color: '#ef4444' },
  { value: 'meeting', label: 'Meeting', color: '#06b6d4' },
  { value: 'tech', label: 'Tech Check', color: '#f97316' },
];

export default function ScheduleSection({ eventId, permissions }: { eventId: string; permissions: any }) {
  const [schedule, setSchedule] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [showCategoryDropdown, setShowCategoryDropdown] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [deleteDayDate, setDeleteDayDate] = useState<string | null>(null);
  const [duplicateDayDate, setDuplicateDayDate] = useState<string | null>(null);
  const [duplicateTargetDate, setDuplicateTargetDate] = useState('');
  const [formData, setFormData] = useState<any>({
    title: '',
    category: 'general',
    date: '',
    start_time: '',
    end_time: '',
    room: '',
    notes: ''
  });

  useEffect(() => {
    loadSchedule();
  }, [eventId]);

  const loadSchedule = async () => {
    const { data } = await supabase
      .from('schedule_entries')
      .select('*')
      .eq('event_id', eventId)
      .order('start_datetime');
    if (data) {
      setSchedule(data);
      if (data.length > 0 && !selectedDate) {
        const firstDate = data[0].start_datetime
          ? data[0].start_datetime.split('T')[0]
          : null;
        setSelectedDate(firstDate);
      }
    }
    setLoading(false);
  };

  const handleAddNew = () => {
    setFormData({
      title: '',
      category: 'general',
      date: selectedDate || '',
      start_time: '',
      end_time: '',
      room: '',
      notes: ''
    });
    setEditingId(null);
    setShowForm(true);
  };

  const handleEdit = (entry: any) => {
    // Extract time directly from ISO string without timezone conversion
    const startTime = entry.start_datetime ? entry.start_datetime.split('T')[1]?.substring(0, 5) : '';
    const endTime = entry.end_datetime ? entry.end_datetime.split('T')[1]?.substring(0, 5) : '';
    const date = entry.start_datetime ? entry.start_datetime.split('T')[0] : '';

    setFormData({
      title: entry.title || '',
      category: entry.category || 'general',
      date: date,
      start_time: startTime,
      end_time: endTime,
      room: entry.room || '',
      notes: entry.notes || ''
    });
    setEditingId(entry.id);
    setShowForm(true);
  };

  const handleSave = async () => {
    try {
      if (!formData.title || !formData.date || !formData.start_time) {
        alert('Please fill in title, date, and start time');
        return;
      }

      // Store times as UTC but without timezone conversion
      // This treats the entered time as a "wall clock" time
      const createISODateTime = (date: string, time: string) => {
        return `${date}T${time}:00Z`;
      };

      const payload = {
        title: formData.title,
        category: formData.category,
        room: formData.room || null,
        notes: formData.notes || null,
        start_datetime: formData.date && formData.start_time
          ? createISODateTime(formData.date, formData.start_time)
          : null,
        end_datetime: formData.date && formData.end_time
          ? createISODateTime(formData.date, formData.end_time)
          : null
      };

      if (editingId) {
        await supabase
          .from('schedule_entries')
          .update(payload)
          .eq('id', editingId);
      } else {
        await supabase
          .from('schedule_entries')
          .insert({ ...payload, event_id: eventId });
      }
      await loadSchedule();
      setShowForm(false);
      setEditingId(null);
    } catch (error) {
      console.error('Error saving schedule entry:', error);
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      await supabase.from('schedule_entries').delete().eq('id', deleteId);
      await loadSchedule();
      setDeleteId(null);
    } catch (error) {
      console.error('Error deleting schedule entry:', error);
    }
  };

  const handleDeleteDay = async () => {
    if (!deleteDayDate) return;
    try {
      const entriesToDelete = schedule.filter(e =>
        e.start_datetime && e.start_datetime.split('T')[0] === deleteDayDate
      );

      for (const entry of entriesToDelete) {
        await supabase.from('schedule_entries').delete().eq('id', entry.id);
      }

      await loadSchedule();
      setDeleteDayDate(null);

      if (selectedDate === deleteDayDate) {
        setSelectedDate(uniqueDates[0] !== deleteDayDate ? uniqueDates[0] : uniqueDates[1] || null);
      }
    } catch (error) {
      console.error('Error deleting day:', error);
    }
  };

  const handleDuplicateDay = async () => {
    if (!duplicateDayDate || !duplicateTargetDate) return;
    try {
      const entriesToDuplicate = schedule.filter(e =>
        e.start_datetime && e.start_datetime.split('T')[0] === duplicateDayDate
      );

      for (const entry of entriesToDuplicate) {
        // Extract time directly from ISO string without timezone conversion
        const startTime = entry.start_datetime ? entry.start_datetime.split('T')[1]?.substring(0, 5) : null;
        const endTime = entry.end_datetime ? entry.end_datetime.split('T')[1]?.substring(0, 5) : null;

        const createISODateTime = (date: string, time: string) => {
          return `${date}T${time}:00Z`;
        };

        const payload = {
          event_id: eventId,
          title: entry.title,
          category: entry.category,
          room: entry.room,
          notes: entry.notes,
          start_datetime: startTime ? createISODateTime(duplicateTargetDate, startTime) : null,
          end_datetime: endTime ? createISODateTime(duplicateTargetDate, endTime) : null,
        };

        await supabase.from('schedule_entries').insert(payload);
      }

      await loadSchedule();
      setDuplicateDayDate(null);
      setDuplicateTargetDate('');
      setSelectedDate(duplicateTargetDate);
    } catch (error) {
      console.error('Error duplicating day:', error);
    }
  };

  const toggleCategory = (category: string) => {
    setSelectedCategories(prev =>
      prev.includes(category)
        ? prev.filter(c => c !== category)
        : [...prev, category]
    );
  };

  const uniqueDates = [...new Set(schedule
    .map(e => e.start_datetime ? e.start_datetime.split('T')[0] : null)
    .filter(Boolean))]
    .sort();

  const categoriesInUse = [...new Set(schedule.map(e => e.category || 'general'))];

  const filteredSchedule = schedule
    .filter(e => {
      const entryDate = e.start_datetime ? e.start_datetime.split('T')[0] : null;
      const matchesDate = !selectedDate || entryDate === selectedDate;
      const matchesCategory = selectedCategories.length === 0 || selectedCategories.includes(e.category || 'general');
      return matchesDate && matchesCategory;
    })
    .sort((a, b) => {
      if (!a.start_datetime) return 1;
      if (!b.start_datetime) return -1;
      return a.start_datetime.localeCompare(b.start_datetime);
    });

  const getCategoryColor = (category: string) => {
    return categories.find(c => c.value === category)?.color || '#6b7280';
  };

  if (loading) {
    return <div className="text-center py-8 text-zinc-500">Loading schedule...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-semibold text-white">Daily Schedules</h2>
        {permissions.canEdit && (
          <Button
            onClick={handleAddNew}
            className="bg-primary-500 hover:bg-primary-600 text-white"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Schedule Item
          </Button>
        )}
      </div>

      {categoriesInUse.length > 0 && (
        <div className="bg-zinc-900/50 border border-zinc-800 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <span className="text-zinc-400 text-sm">Filter by Category:</span>
              <div className="relative">
                <button
                  onClick={() => setShowCategoryDropdown(!showCategoryDropdown)}
                  className="flex items-center gap-2 px-4 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-white hover:bg-zinc-700 transition-colors"
                >
                  <span className="text-sm">
                    {selectedCategories.length === 0
                      ? 'All Categories'
                      : `${selectedCategories.length} selected`}
                  </span>
                  <ChevronDown className="w-4 h-4" />
                </button>

                {showCategoryDropdown && (
                  <>
                    <div
                      className="fixed inset-0 z-10"
                      onClick={() => setShowCategoryDropdown(false)}
                    />
                    <div className="absolute top-full left-0 mt-2 w-64 bg-zinc-900 border border-zinc-700 rounded-lg shadow-xl z-20 py-2">
                      {categoriesInUse.map((catValue) => {
                        const cat = categories.find(c => c.value === catValue);
                        if (!cat) return null;
                        return (
                          <label
                            key={catValue}
                            className="flex items-center gap-3 px-4 py-2 hover:bg-zinc-800 cursor-pointer transition-colors"
                          >
                            <input
                              type="checkbox"
                              checked={selectedCategories.includes(catValue)}
                              onChange={() => toggleCategory(catValue)}
                              className="w-4 h-4 rounded border-zinc-600 bg-zinc-800 text-primary-500"
                            />
                            <div
                              className="w-3 h-3 rounded"
                              style={{ backgroundColor: cat.color }}
                            />
                            <span className="text-sm text-white">{cat.label}</span>
                          </label>
                        );
                      })}
                    </div>
                  </>
                )}
              </div>
            </div>
            {selectedCategories.length > 0 && (
              <Button
                variant="outline"
                className="border-zinc-700 text-zinc-300 hover:bg-zinc-800 hover:text-white"
                onClick={() => setSelectedCategories([])}
              >
                Clear Filters
              </Button>
            )}
          </div>
        </div>
      )}

      {uniqueDates.length > 0 && (
        <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-visible">
          {uniqueDates.map((date) => (
            <div key={date} className="flex items-center gap-1">
              <button
                onClick={() => setSelectedDate(date)}
                className={`px-6 py-2 rounded-lg border transition-colors whitespace-nowrap ${
                  selectedDate === date
                    ? 'bg-primary-500 border-primary-500 text-white'
                    : 'bg-zinc-900/50 border-zinc-700 text-zinc-300 hover:bg-zinc-800 hover:border-zinc-600'
                }`}
              >
                {format(parseISO(date! + 'T00:00:00'), 'EEE MMM dd, yyyy')}
              </button>
              {permissions.canEdit && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button className="p-1.5 text-zinc-400 hover:text-white hover:bg-zinc-800 rounded transition-colors border border-zinc-700/50 hover:border-zinc-600">
                      <MoreVertical className="w-4 h-4" />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent className="bg-zinc-900 border-zinc-800">
                    <DropdownMenuItem
                      onClick={() => {
                        setDuplicateDayDate(date);
                        setDuplicateTargetDate('');
                      }}
                      className="text-zinc-300 hover:bg-zinc-800 hover:text-white cursor-pointer"
                    >
                      <Copy className="w-4 h-4 mr-2" />
                      Duplicate Day
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() => setDeleteDayDate(date)}
                      className="text-red-400 hover:bg-zinc-800 hover:text-red-300 cursor-pointer"
                    >
                      <Trash2 className="w-4 h-4 mr-2" />
                      Delete Day
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
            </div>
          ))}
        </div>
      )}

      {filteredSchedule.length === 0 ? (
        <div className="bg-zinc-900/50 border border-zinc-800 rounded-lg p-12 text-center">
          <p className="text-zinc-500">
            {schedule.length === 0 ? 'No schedule entries yet' : 'No entries match your filters'}
          </p>
          {permissions.canEdit && schedule.length === 0 && (
            <Button
              onClick={handleAddNew}
              className="mt-4 bg-primary-500 hover:bg-primary-600 text-white"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add First Schedule Item
            </Button>
          )}
        </div>
      ) : (
        <>
          <div className="hidden md:block bg-zinc-900/50 border border-zinc-800 rounded-lg overflow-hidden">
            <table className="w-full">
              <thead className="bg-zinc-800/50 border-b border-zinc-700">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-zinc-400 uppercase tracking-wider">
                    Item
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-zinc-400 uppercase tracking-wider">
                    Start
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-zinc-400 uppercase tracking-wider">
                    End
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-zinc-400 uppercase tracking-wider">
                    Category
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-zinc-400 uppercase tracking-wider">
                    Room
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-zinc-400 uppercase tracking-wider">
                    Notes
                  </th>
                  {permissions.canEdit && (
                    <th className="px-6 py-3 text-left text-xs font-medium text-zinc-400 uppercase tracking-wider">
                      Actions
                    </th>
                  )}
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-800">
                {filteredSchedule.map((entry, index) => {
                  const isShowCategory = entry.category === 'show';
                  const categoryColor = getCategoryColor(entry.category || 'general');
                  return (
                    <tr
                      key={entry.id}
                      className={`${
                        isShowCategory
                          ? 'bg-primary-500/20'
                          : index % 2 === 0
                          ? 'bg-zinc-900/30'
                          : 'bg-zinc-900/50'
                      } hover:bg-zinc-800/50 transition-colors border-l-4`}
                      style={{ borderLeftColor: categoryColor }}
                    >
                      <td className="px-6 py-4 text-sm text-white">
                        {entry.title}
                      </td>
                      <td className="px-6 py-4 text-sm text-zinc-300">
                        {entry.start_datetime && (() => {
                          const time = entry.start_datetime.split('T')[1]?.substring(0, 5);
                          if (!time) return '';
                          const [hours, minutes] = time.split(':');
                          const h = parseInt(hours);
                          const ampm = h >= 12 ? 'PM' : 'AM';
                          const displayHour = h === 0 ? 12 : h > 12 ? h - 12 : h;
                          return `${displayHour}:${minutes} ${ampm}`;
                        })()}
                      </td>
                      <td className="px-6 py-4 text-sm text-zinc-300">
                        {entry.end_datetime && (() => {
                          const time = entry.end_datetime.split('T')[1]?.substring(0, 5);
                          if (!time) return '';
                          const [hours, minutes] = time.split(':');
                          const h = parseInt(hours);
                          const ampm = h >= 12 ? 'PM' : 'AM';
                          const displayHour = h === 0 ? 12 : h > 12 ? h - 12 : h;
                          return `${displayHour}:${minutes} ${ampm}`;
                        })()}
                      </td>
                      <td className="px-6 py-4 text-sm">
                        <span
                          className="px-2 py-1 rounded text-xs font-medium text-white"
                          style={{ backgroundColor: categoryColor }}
                        >
                          {categories.find(c => c.value === entry.category)?.label || entry.category}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-zinc-300">
                        {entry.room || '-'}
                      </td>
                      <td className="px-6 py-4 text-sm text-zinc-400">
                        {entry.notes || ''}
                      </td>
                      {permissions.canEdit && (
                        <td className="px-6 py-4 text-sm">
                          <div className="flex gap-2">
                            <button
                              onClick={() => handleEdit(entry)}
                              className="text-primary-400 hover:text-primary-300 transition-colors"
                            >
                              <Edit className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => setDeleteId(entry.id)}
                              className="text-red-400 hover:text-red-300 transition-colors"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="md:hidden space-y-3">
            {filteredSchedule.map((entry) => {
              const isShowCategory = entry.category === 'show';
              const categoryColor = getCategoryColor(entry.category || 'general');
              const categoryLabel = categories.find(c => c.value === entry.category)?.label || entry.category;

              return (
                <div
                  key={entry.id}
                  className={`${
                    isShowCategory ? 'bg-primary-500/20' : 'bg-zinc-900/50'
                  } border border-zinc-800 rounded-lg overflow-hidden border-l-4`}
                  style={{ borderLeftColor: categoryColor }}
                >
                  <div className="p-4 space-y-3">
                    <div className="flex items-start justify-between gap-3">
                      <h3 className="text-base font-semibold text-white flex-1">
                        {entry.title}
                      </h3>
                      {permissions.canEdit && (
                        <div className="flex gap-2 flex-shrink-0">
                          <button
                            onClick={() => handleEdit(entry)}
                            className="text-primary-400 hover:text-primary-300 transition-colors p-1"
                          >
                            <Edit className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => setDeleteId(entry.id)}
                            className="text-red-400 hover:text-red-300 transition-colors p-1"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      )}
                    </div>

                    <div className="flex items-center gap-2 text-sm">
                      <span className="text-zinc-400">
                        {entry.start_datetime && (() => {
                          const time = entry.start_datetime.split('T')[1]?.substring(0, 5);
                          if (!time) return '';
                          const [hours, minutes] = time.split(':');
                          const h = parseInt(hours);
                          const ampm = h >= 12 ? 'PM' : 'AM';
                          const displayHour = h === 0 ? 12 : h > 12 ? h - 12 : h;
                          return `${displayHour}:${minutes} ${ampm}`;
                        })()}
                      </span>
                      {entry.end_datetime && (
                        <>
                          <span className="text-zinc-600">→</span>
                          <span className="text-zinc-400">
                            {(() => {
                              const time = entry.end_datetime.split('T')[1]?.substring(0, 5);
                              if (!time) return '';
                              const [hours, minutes] = time.split(':');
                              const h = parseInt(hours);
                              const ampm = h >= 12 ? 'PM' : 'AM';
                              const displayHour = h === 0 ? 12 : h > 12 ? h - 12 : h;
                              return `${displayHour}:${minutes} ${ampm}`;
                            })()}
                          </span>
                        </>
                      )}
                    </div>

                    <div className="flex flex-wrap items-center gap-2">
                      <span
                        className="px-2 py-1 rounded text-xs font-medium text-white"
                        style={{ backgroundColor: categoryColor }}
                      >
                        {categoryLabel}
                      </span>
                      {entry.room && (
                        <span className="px-2 py-1 rounded text-xs bg-zinc-800 text-zinc-300">
                          {entry.room}
                        </span>
                      )}
                    </div>

                    {entry.notes && (
                      <p className="text-sm text-zinc-400 pt-2 border-t border-zinc-800">
                        {entry.notes}
                      </p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}

      {showForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-zinc-900 border border-zinc-800 rounded-lg w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
            <div className="p-4 border-b border-zinc-800 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-white">
                {editingId ? 'Edit' : 'Add'} Schedule Item
              </h3>
              <button
                onClick={() => setShowForm(false)}
                className="text-zinc-400 hover:text-white"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6 space-y-4 overflow-y-auto">
              <div>
                <Label className="text-zinc-300 text-sm">Title *</Label>
                <Input
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="e.g., Sound Check, Doors Open"
                  className="mt-1.5 bg-zinc-800/50 border-zinc-700 text-white"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-zinc-300 text-sm">Category *</Label>
                  <Select
                    value={formData.category}
                    onValueChange={(value) => setFormData({ ...formData, category: value })}
                  >
                    <SelectTrigger className="mt-1.5 bg-zinc-800/50 border-zinc-700 text-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-zinc-900 border-zinc-800">
                      {categories.map(cat => (
                        <SelectItem key={cat.value} value={cat.value}>
                          <div className="flex items-center gap-2">
                            <div
                              className="w-3 h-3 rounded"
                              style={{ backgroundColor: cat.color }}
                            />
                            {cat.label}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-zinc-300 text-sm">Room</Label>
                  <Input
                    value={formData.room}
                    onChange={(e) => setFormData({ ...formData, room: e.target.value })}
                    placeholder="Room/Location"
                    className="mt-1.5 bg-zinc-800/50 border-zinc-700 text-white"
                  />
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label className="text-zinc-300 text-sm">Date *</Label>
                  <Input
                    type="date"
                    value={formData.date}
                    onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                    className="mt-1.5 bg-zinc-800/50 border-zinc-700 text-white"
                  />
                </div>
                <div>
                  <Label className="text-zinc-300 text-sm">Start Time *</Label>
                  <Input
                    type="time"
                    value={formData.start_time}
                    onChange={(e) => setFormData({ ...formData, start_time: e.target.value })}
                    className="mt-1.5 bg-zinc-800/50 border-zinc-700 text-white"
                  />
                </div>
                <div>
                  <Label className="text-zinc-300 text-sm">End Time</Label>
                  <Input
                    type="time"
                    value={formData.end_time}
                    onChange={(e) => setFormData({ ...formData, end_time: e.target.value })}
                    className="mt-1.5 bg-zinc-800/50 border-zinc-700 text-white"
                  />
                </div>
              </div>

              <div>
                <Label className="text-zinc-300 text-sm">Notes</Label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  rows={3}
                  placeholder="Additional notes..."
                  className="mt-1.5 w-full bg-zinc-800/50 border border-zinc-700 text-white rounded-md px-3 py-2 text-sm"
                />
              </div>

              <div className="flex gap-3 pt-4">
                <Button
                  onClick={handleSave}
                  className="flex-1 bg-primary-500 hover:bg-primary-600 text-white"
                >
                  {editingId ? 'Save Changes' : 'Add Schedule Item'}
                </Button>
                <Button
                  onClick={() => setShowForm(false)}
                  variant="outline"
                  className="border-zinc-700 text-zinc-300 hover:bg-zinc-800"
                >
                  Cancel
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent className="bg-zinc-900 border-zinc-800">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">Delete Schedule Item?</AlertDialogTitle>
            <AlertDialogDescription className="text-zinc-400">
              This will permanently remove this item from the schedule.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setDeleteId(null)} className="bg-zinc-800 text-white border-zinc-700 hover:bg-zinc-700">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-600 text-white hover:bg-red-700"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!deleteDayDate} onOpenChange={() => setDeleteDayDate(null)}>
        <AlertDialogContent className="bg-zinc-900 border-zinc-800">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">Delete Entire Day?</AlertDialogTitle>
            <AlertDialogDescription className="text-zinc-400">
              This will permanently remove all {schedule.filter(e => e.start_datetime && e.start_datetime.split('T')[0] === deleteDayDate).length} schedule entries for {deleteDayDate && format(parseISO(deleteDayDate + 'T00:00:00'), 'EEE MMM dd, yyyy')}.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setDeleteDayDate(null)} className="bg-zinc-800 text-white border-zinc-700 hover:bg-zinc-700">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteDay}
              className="bg-red-600 text-white hover:bg-red-700"
            >
              Delete Day
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {duplicateDayDate && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-zinc-900 border border-zinc-800 rounded-lg w-full max-w-md">
            <div className="p-4 border-b border-zinc-800 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-white">Duplicate Day</h3>
              <button
                onClick={() => {
                  setDuplicateDayDate(null);
                  setDuplicateTargetDate('');
                }}
                className="text-zinc-400 hover:text-white"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6 space-y-4">
              <p className="text-zinc-400 text-sm">
                Copy all {schedule.filter(e => e.start_datetime && e.start_datetime.split('T')[0] === duplicateDayDate).length} schedule entries from {format(parseISO(duplicateDayDate + 'T00:00:00'), 'EEE MMM dd, yyyy')} to a new date.
              </p>

              <div>
                <Label className="text-zinc-300 text-sm">Target Date</Label>
                <Input
                  type="date"
                  value={duplicateTargetDate}
                  onChange={(e) => setDuplicateTargetDate(e.target.value)}
                  className="mt-1.5 bg-zinc-800/50 border-zinc-700 text-white"
                />
              </div>

              <div className="flex gap-3 pt-4">
                <Button
                  onClick={handleDuplicateDay}
                  disabled={!duplicateTargetDate}
                  className="flex-1 bg-primary-500 hover:bg-primary-600 text-white disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Copy className="w-4 h-4 mr-2" />
                  Duplicate Day
                </Button>
                <Button
                  onClick={() => {
                    setDuplicateDayDate(null);
                    setDuplicateTargetDate('');
                  }}
                  variant="outline"
                  className="border-zinc-700 text-zinc-300 hover:bg-zinc-800"
                >
                  Cancel
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
